import { RETURN_ELIGIBILITY_MESSAGES } from '@/constants/appReturn.const'

/**
 * Customer Return Eligibility Utilities
 *
 * Determines return eligibility and messaging based on return window data
 * from individual line items. Each item can have different return policies.
 */

/**
 * Determine return eligibility message based on return window data
 *
 * @param {Object} returnWindow - Return window object from line item
 * @param {boolean} returnWindow.refund - Whether refund is available
 * @param {boolean} returnWindow.inStoreCredit - Whether store credit is available
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.enforceReturnWindow=true] - Whether the return window is enforced.
 *   When false, items outside the return window are still allowed to return.
 *   Controlled by plan feature RETURN_WINDOW_ENFORCEMENT.
 * @param {Array} [options.returnableFulfillments] - Array of fulfillments for the item.
 *   When empty, the item has not been fulfilled/shipped yet, so we skip the
 *   OUTSIDE_POLICY message since there is no return window to evaluate.
 * @returns {Object} Result object with returnOk boolean and eligibilityMessage string
 *
 * @example
 * // Refund available
 * getReturnEligibilityMessage({ refund: true, inStoreCredit: false })
 * // Returns: { returnOk: true, eligibilityMessage: "This order is eligible for return for a full refund." }
 *
 * @example
 * // Outside return window
 * getReturnEligibilityMessage({ refund: false, inStoreCredit: false })
 * // Returns: { returnOk: false, eligibilityMessage: "This order is outside the retailer's return policy." }
 *
 * @example
 * // Outside return window but enforcement disabled
 * getReturnEligibilityMessage({ refund: false, inStoreCredit: false }, { enforceReturnWindow: false })
 * // Returns: { returnOk: true, eligibilityMessage: '' }
 */
export function getReturnEligibilityMessage(returnWindow, options = {}) {
  const { enforceReturnWindow = true, returnableFulfillments } = options

  if (!returnWindow) {
    return {
      returnOk: false,
      eligibilityMessage: '',
    }
  }

  // If returnableFulfillments is an empty array, the item has not been
  // fulfilled/shipped yet — there is no return window to evaluate, so
  // we should not show the OUTSIDE_POLICY message.
  if (Array.isArray(returnableFulfillments) && returnableFulfillments.length === 0) {
    return {
      returnOk: false,
      eligibilityMessage: '',
    }
  }

  // When return window enforcement is disabled, allow the return anyway
  if (!enforceReturnWindow) {
    return {
      returnOk: true,
      eligibilityMessage: RETURN_ELIGIBILITY_MESSAGES.OUTSIDE_POLICY_NOT_ENFORCED,
    }
  }

  const { refund, inStoreCredit } = returnWindow
  const returnOk = refund || inStoreCredit

  if (!returnOk) {
    return {
      returnOk: false,
      eligibilityMessage: RETURN_ELIGIBILITY_MESSAGES.OUTSIDE_POLICY,
    }
  }

  // Refund only (no store credit)
  if (refund && !inStoreCredit) {
    return {
      returnOk: true,
      eligibilityMessage: RETURN_ELIGIBILITY_MESSAGES.REFUND_ONLY,
    }
  }

  // Store credit only (no refund)
  if (!refund && inStoreCredit) {
    return {
      returnOk: true,
      eligibilityMessage: RETURN_ELIGIBILITY_MESSAGES.STORE_CREDIT_ONLY,
    }
  }

  // Both options available
  return {
    returnOk: true,
    eligibilityMessage: RETURN_ELIGIBILITY_MESSAGES.BOTH_OPTIONS,
  }
}
